import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { RouterProvider, useSubmit } from "react-router-dom";
import { useWindowSize } from "react-use";
import {
  ActionIcon,
  Button,
  ColorSchemeScript,
  createTheme,
  em,
  MantineColorsTuple,
  MantineProvider,
  Menu,
  Modal,
  PasswordInput,
  Table,
  Tabs,
  Text,
  TextInput,
} from "@mantine/core";
import { DatePickerInput, DatesProvider, DateTimePicker } from "@mantine/dates";
import { useOrientation } from "@mantine/hooks";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { Analytics } from "@vercel/analytics/react";
import { io } from "socket.io-client";

import { showErrorNotification } from "@utils/showNotification";

import { useSubmitTelemetry } from "@hooks/mutations/useSubmitTelemetry";
import { useTelemetryStore } from "@hooks/store/useTelemetryStore";

import "dayjs/locale/en";
import "dayjs/locale/fr";

import { GlobalStyles } from "./styles/GlobalStyles.styled";
import { AUTO_CLOSE } from "./constants";
import { router } from "./router";

import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/charts/styles.css";

// From logo color
const primary: MantineColorsTuple = [
  "#e2fdfd",
  "#d4f6f4",
  "#afe8e7",
  "#88dad9",
  "#66cfcd",
  "#4fc7c5",
  "#40c4c2",
  "#2dadab",
  "#199a98",
  "#008684",
];

const accent: MantineColorsTuple = [
  "#f0eaff",
  "#dacfff",
  "#b19cff",
  "#8664ff",
  "#6136ff",
  "#4a18ff",
  "#3d07ff",
  "#2f00e5",
  "#2800cd",
  "#1c00b5",
];

const themeOverride = createTheme({
  // These should be in sync with the breakpoints in styles/Utilities.styled.ts
  breakpoints: {
    md: em(1024),
    lg1: em(1200),
    lg2: em(1440),
    lg3: em(1600),
    lg4: em(1920),
  },
  primaryColor: "primary",
  black: "hsl(242, 98%, 16%)",
  colors: {
    primary,
    accent,
  },
  fontFamily: "Poppins, sans-serif",
  components: {
    Text: Text.extend({
      defaultProps: { lh: 1 },
    }),
    TextInput: TextInput.extend({
      classNames: {
        input: "mantine-text-input",
        root: "mantine-text-input-wrapper",
      },
    }),
    PasswordInput: PasswordInput.extend({
      classNames: {
        input: "mantine-password-input",
        wrapper: "mantine-password-input-wrapper",
      },
    }),
    Button: Button.extend({
      defaultProps: { loaderProps: { type: "dots" } },
      classNames: {
        root: "mantine-button",
      },
    }),
    ActionIcon: ActionIcon.extend({
      defaultProps: { loaderProps: { type: "dots" } },
    }),
    Menu: Menu.extend({
      defaultProps: { transitionProps: { transition: "pop" } },
    }),
    Tabs: Tabs.extend({
      classNames: {
        tab: "mantine-tab",
      },
    }),
    // ! Nothing works here
    // ToolTip: Tooltip.extend({
    //   defaultProps: {
    //     events: { hover: true, focus: true, touch: true }, //* Touch still doesn't work unless events object is re-declared directly on component
    //     withArrow: true,
    //   },
    //   // classNames: {
    //   //   tooltip: "mantine-tooltip", //* This also doesn't work
    //   // },
    //   // styles: {
    //   //   tooltip: { fontSize: 40, color: "red" }, //* This too doesn't work
    //   // },
    // }),
    DatePickerInput: DatePickerInput.extend({
      defaultProps: { valueFormat: "DD/MM/YYYY", highlightToday: true },
      classNames: {
        input: "mantine-date-picker-input",
        root: "mantine-date-picker-input-wrapper",
      },
    }),
    DateTimePicker: DateTimePicker.extend({
      // defaultProps: { valueFormat: "DD/MM/YYYY, HH:mm" },
      classNames: {
        input: "mantine-date-time-picker",
        root: "mantine-date-time-picker-wrapper",
      },
    }),
    Notifications: Notifications.extend({
      defaultProps: { zIndex: 999999 }, // todo: Replace with z-index constants
      classNames: {
        notification: "mantine-notification",
      },
    }),
    Modal: Modal.extend({
      styles: {
        header: {
          // borderBottom: "1px solid var(--mantine-color-gray-4)", //* Currently doesn't look good with modals having info and "add files" tabs, but there's hope
        },
        body: {
          maxHeight: 600 - 60,
          // minHeight: 450 - 60,
          // display: "flex",
        }, //* 60 is the modal header height, convert to CSS variable later
      },
    }),
    Table: Table.extend({
      styles: {
        thead: {
          height: 67,
        },
        th: {
          fontWeight: 500,
          fontSize: 14,
          lineHeight: 1.5,
        },
        td: {
          fontSize: 14,
          lineHeight: 1.5,
        },
        tr: {
          height: 60,
        },
      },
    }),
  },
});

export const App = () => {
  const { i18n } = useTranslation();
  const store = useTelemetryStore();
  const { width, height } = useWindowSize();
  // const os = useOs(); // Returns "undetermined" for some reason even though on their website it correctly detects my OS
  const { type: orientation } = useOrientation();
  const socketUrl = import.meta.env.VITE_SOCKET_URL;
  const queryClient = useQueryClient();
  const { mutate: submitTelemetry } = useSubmitTelemetry();

  // Telemetry
  useEffect(() => {
    // Set these if they are not already set before next useEffect submits telemetry
    // If a different user signs in, these will be cleared, so this useEffect is needed before submission.

    store.setScreenOrientation(orientation);
    store.setScreenResolution(`${width}x${height}`);
    store.setLanguage(i18n.language);
  }, [width, height, orientation, i18n.language]);

  useEffect(() => {
    const telemetryInterval = store.startTelemetryInterval(() =>
      submitTelemetry()
    );

    // todo: Sockets are no longer used, comment this code out
    // Appointments
    const socket = io(socketUrl, {
      path: import.meta.env.VITE_SOCKET_PATH,
      transports: ["websocket"],
      reconnectionDelay: 10000,
      reconnectionDelayMax: 30000,
      reconnectionAttempts: 5,
    });

    socket.on("connect", () => {
      import.meta.env.DEV && console.log("[Socket] - Connected");
    });

    socket.on("yes", (data: any) => console.log(data));

    socket.on("addToIncomingAppointment", (data: any) => {
      import.meta.env.DEV &&
        console.log("addToIncomingAppointment triggered:", data);
      const parsedData = JSON.parse(data);
      queryClient.setQueryData(
        [
          "appointments",
          { category: parsedData.category },
          { status: parsedData.status },
        ],
        (oldDataRes: any) => {
          // console.log(oldDataRes);
          const oldArray = oldDataRes.data;

          return {
            ...oldDataRes,
            data: [...oldArray, parsedData].toSorted(
              (a, b) =>
                new Date(b.updated_at).getTime() -
                new Date(a.updated_at).getTime()
            ),
          };
        }
      );
      // queryClient.invalidateQueries({ queryKey: appointmentQKeys.all() });
    });

    // socket.on("updateIncomingAppointment", (data: any) => {
    //   const category =
    //     storeTabs.appointmentCategoryTab === "Clinic" ? "clinic" : "homecare";
    //   const status =
    //     storeTabs.appointmentStatusTab === "Incoming" ? "pending" : "booked";
    //   console.log("updateIncomingAppointment triggered:", data);
    //   const parsedData = JSON.parse(data);
    //   queryClient.setQueryData(
    //     [
    //       "appointments",
    //       {
    //         // prettier-ignore
    //         "category": category,
    //       },
    //       {
    //         // prettier-ignore
    //         "status": status,
    //       },
    //     ],
    //     (oldDataRes: any) => {
    //       // console.log("old data", oldDataRes);
    //       const idToReplace = parsedData._id;
    //       const newStatus = parsedData.status;
    //       const newDoctor = parsedData.doctor_assigned;
    //       // const newDate = parsedData.details.date ?? parsedData.details.service_date;
    //       const clinicDate = parsedData?.details?.date;
    //       const homecareDate = parsedData?.details?.service_date;
    //       const newUpdatedAt = parsedData.updated_at;
    //       const oldArray = oldDataRes.data;

    //       const indexToReplace = oldArray.findIndex(
    //         (item: any) => item.id === idToReplace
    //       );

    //       //* Remove item from array if their status is "cancelled"
    //       if (
    //         (status === "pending" && newStatus !== "pending") ||
    //         newStatus === "cancelled"
    //       ) {
    //         const newArray = [...oldArray];
    //         newArray.splice(indexToReplace, 1);
    //         return { ...oldDataRes, data: newArray };
    //       }

    //       if (indexToReplace !== -1) {
    //         //* Create a new array with the replaced element
    //         const newArray = [...oldArray];
    //         newArray[indexToReplace] = {
    //           ...newArray[indexToReplace],
    //           ...(newStatus && { status: newStatus }),
    //           ...(newDoctor && { doctor_assigned: newDoctor }),
    //           ...(clinicDate && {
    //             details: {
    //               ...newArray[indexToReplace].details,
    //               date: clinicDate,
    //             },
    //           }),
    //           ...(homecareDate && {
    //             details: {
    //               ...newArray[indexToReplace].details,
    //               service_date: homecareDate,
    //             },
    //           }),
    //           updated_at: newUpdatedAt,
    //         };

    //         // console.log("updated Array:", newArray);
    //         return {
    //           ...oldDataRes,
    //           data: newArray.toSorted(
    //             (a, b) =>
    //               new Date(b.updated_at).getTime() -
    //               new Date(a.updated_at).getTime()
    //           ),
    //         };
    //       } else {
    //         console.error(
    //           `[Socket] - Appointment with ID: ${idToReplace} not found.`
    //         );
    //       }
    //     }
    //   );
    // queryClient.invalidateQueries({ queryKey: appointmentQKeys.all() });
    // });

    socket.on("connect_error", () => {
      console.error("[SOCKET] - Connection error");

      showErrorNotification({
        title: "oops",
        message: "real-time-connection-error",
      });
    });

    socket.on("disconnect", reason => console.log("[Socket]", reason));

    return () => {
      socket.close();
      clearInterval(telemetryInterval);
    };
  }, []);

  // if (width < 700) {
  // console.log("Is less than 1024");
  //   return (
  //     <>
  //       <GlobalStyles />
  //       <SmallScreenIndicator />
  //     </>
  //   );
  // }

  return (
    <>
      <Analytics />
      <ColorSchemeScript defaultColorScheme="light" />
      <MantineProvider theme={themeOverride} defaultColorScheme="light">
        <Notifications
          limit={5}
          position="top-right"
          w={350}
          autoClose={AUTO_CLOSE}
        />
        <DatesProvider settings={{ locale: i18n.language }}>
          <ModalsProvider>
            <GlobalStyles />
            <RouterProvider router={router} />
          </ModalsProvider>
        </DatesProvider>
      </MantineProvider>
    </>
  );
};
